import { useEffect, useMemo, useRef, useState } from 'react'
import { FaEnvelope } from 'react-icons/fa'
import Button from '../atoms/Buttons'
import CloseIcon from '../../assets/icons/actions/close.svg'
import { createToast } from '../../utils/Toast'
import { shareFilesByEmail } from '../../utils/fileBrowserApi'

const Toast = createToast()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INTERNAL_DOMAIN = '@saddlefit.io'

export default function ShareByEmailModal({ items, onClose }) {
  const modalRef = useRef(null)
  const [emailsInput, setEmailsInput] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target) && !sending) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose, sending])

  const emails = useMemo(
    () =>
      emailsInput
        .split(/[\s,;]+/)
        .map(e => e.trim().toLowerCase())
        .filter(Boolean),
    [emailsInput],
  )

  const invalidEmails = emails.filter(e => !EMAIL_RE.test(e))
  const externalEmails = emails.filter(
    e => EMAIL_RE.test(e) && !e.endsWith(INTERNAL_DOMAIN),
  )
  const privateItems = items.filter(i => i.bucket === 'private')

  const canSend = emails.length > 0 && !invalidEmails.length && !sending

  const handleSend = async () => {
    if (!canSend) return
    setSending(true)
    try {
      const res = await shareFilesByEmail({ emails, items, message })
      Toast.fire({
        icon: 'success',
        title: `${res.fileCount} file${res.fileCount > 1 ? 's' : ''} shared with ${res.recipients.length} recipient${res.recipients.length > 1 ? 's' : ''}`,
      })
      onClose()
    } catch (err) {
      console.error('Share failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to share files' })
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <div className="w-full h-full fixed top-0 left-0 z-[60] bg-[#000] opacity-60"></div>

      <div
        ref={modalRef}
        className="w-[340px] sm:w-[520px] max-w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto rounded-[26px] bg-[#fff] p-8 shadow-2xl z-[70] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 font-montserrat"
      >
        <div onClick={!sending ? onClose : undefined} className="absolute top-5 right-5">
          <img
            src={CloseIcon}
            alt="Close"
            className={`w-3 h-3 sm:w-4 sm:h-4 ${sending ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          />
        </div>

        <h4 className="text-lg font-semibold text-gray-800 m-0 mb-1">
          Share via Email
        </h4>
        <p className="text-sm text-gray-500 m-0 mb-4">
          Sharing {items.length} file{items.length > 1 ? 's' : ''}
          {privateItems.length > 0 && (
            <span> ({privateItems.length} private — links expire in 1 hour)</span>
          )}
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Recipients
        </label>
        <input
          type="text"
          value={emailsInput}
          onChange={e => setEmailsInput(e.target.value)}
          placeholder="name@saddlefit.io, other@example.com"
          disabled={sending}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2b364b]"
        />
        {invalidEmails.length > 0 && (
          <p className="text-red-500 text-xs mt-1 mb-0">
            Invalid: {invalidEmails.join(', ')}
          </p>
        )}
        {externalEmails.length > 0 && privateItems.length > 0 && (
          <p className="text-amber-600 text-xs mt-1 mb-0">
            {externalEmails.join(', ')} {externalEmails.length > 1 ? 'are' : 'is'} outside {INTERNAL_DOMAIN} — private files will be shared as links anyone can open until they expire in 1 hour.
          </p>
        )}

        <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">
          Message (optional)
        </label>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="Add a note for the recipients…"
          disabled={sending}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2b364b] resize-none"
        />

        <div className="flex justify-end gap-2 mt-6">
          <Button
            type="button"
            primaryOutline
            onClick={onClose}
            disabled={sending}
            className="create-saddle-btn !w-auto px-6 cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="button"
            primary
            AppendIcon={FaEnvelope}
            loading={sending}
            disabled={!canSend}
            onClick={handleSend}
            className="create-saddle-btn !w-auto px-6 cursor-pointer"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  )
}
